import React from "react";
import type {
  AgendaItem,
  PublicComment,
  CommentModes,
  PublicCommentConfig
} from "../types";
import { BubbleIcon, ChevronIcon } from "./icons";
import CommentCard from "./CommentCard";
import ItemComposer from "./ItemComposer";
import { countPublicComments } from "../utils";
import type { UseAgendaCommentsReturn } from "../hooks/useAgendaComments";

interface Props {
  item: AgendaItem;
  comments: PublicComment[];
  modes: CommentModes;
  isOpen: boolean;
  commenterName: string;
  characterLimit: number;
  staffBadgeLabel: string;
  config: PublicCommentConfig;
  state: UseAgendaCommentsReturn;
}

function sortComments(
  comments: PublicComment[],
  mode: "top" | "new"
): PublicComment[] {
  const arr = comments.slice();
  arr.sort((a, b) =>
    mode === "top"
      ? b.votes - a.votes
      : Date.parse(b.createdAtUtc) - Date.parse(a.createdAtUtc)
  );
  // The author's own pending (held) comment always pins to the top.
  arr.sort((a, b) => (b.pending ? 1 : 0) - (a.pending ? 1 : 0));
  return arr;
}

const AgendaCommentRow: React.FC<Props> = ({
  item,
  comments,
  modes,
  isOpen,
  commenterName,
  characterLimit,
  staffBadgeLabel,
  config,
  state
}) => {
  const n = countPublicComments(comments);
  const sortMode = state.sortFor(item.id);
  const draft = state.composeFor(item.id);
  const hasComments = n > 0;
  const hasPending = comments.some((c) => c.pending);

  return (
    <div className={`pc-ag-row${isOpen ? " pc-open" : ""}`}>
      <div
        className="pc-ag-rowhead"
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-controls={`pc-panel-${item.id}`}
        onClick={() => state.toggleItem(item.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            state.toggleItem(item.id);
          }
        }}
      >
        <span className="pc-ag-code">{item.agenda}</span>
        <span className="pc-ag-title">{item.title}</span>
        {hasComments ? (
          <span className="pc-ag-count pc-has">
            <BubbleIcon />
            {n}
            {n === 1 ? " comment" : " comments"}
          </span>
        ) : (
          <span className="pc-ag-count pc-none">No comments yet</span>
        )}
        <ChevronIcon className="pc-ag-chev" />
      </div>

      {isOpen && (
        <div className="pc-ag-panel" id={`pc-panel-${item.id}`}>
          <div className="pc-panel-bar">
            <span className="pc-panel-lbl">
              {hasComments
                ? `${n}${n === 1 ? " comment" : " comments"}`
                : "Comments"}
            </span>
            {hasComments && (
              <div
                className="pc-sortseg"
                role="group"
                aria-label="Sort comments"
              >
                <button
                  type="button"
                  className={sortMode === "top" ? "pc-on" : ""}
                  aria-pressed={sortMode === "top"}
                  onClick={() => state.setSort(item.id, "top")}
                >
                  Top
                </button>
                <button
                  type="button"
                  className={sortMode === "new" ? "pc-on" : ""}
                  aria-pressed={sortMode === "new"}
                  onClick={() => state.setSort(item.id, "new")}
                >
                  Newest
                </button>
              </div>
            )}
          </div>

          {hasComments || hasPending ? (
            <div className="pc-thread">
              {sortComments(comments, sortMode).map((c) => (
                <CommentCard
                  key={c.id}
                  comment={c}
                  modes={modes}
                  onVote={(cid) => state.toggleVote(item.id, cid)}
                  onFlag={(cid) => state.flagComment(cid)}
                  replyOpen={state.replyOpenFor(c.id)}
                  onOpenReply={state.openReply}
                  onCancelReply={state.cancelReply}
                  replyDraft={state.replyDraftFor(c.id)}
                  onReplyDraftChange={state.setReplyDraft}
                  onSendReply={(parentId) => state.sendReply(item.id, parentId)}
                  replySubmitting={state.submittingReplyId === c.id}
                  reported={state.reportedFor(c.id)}
                  staffBadgeLabel={staffBadgeLabel}
                  canPostAsAdmin={state.canPostAsAdmin}
                  orgLabel={state.orgLabel}
                  replyAsAdmin={state.replyAsAdminFor(c.id)}
                  onToggleReplyAsAdmin={state.setReplyAsAdmin}
                />
              ))}
            </div>
          ) : (
            <div className="pc-empty">
              No public comments on this item yet. Be the first to comment.
            </div>
          )}

          <ItemComposer
            item={item}
            modes={modes}
            text={draft.text}
            position={draft.position}
            commenterName={commenterName}
            submitting={state.submittingItem === item.id}
            showModNotice={state.modNoticeItems.includes(item.id)}
            characterLimit={characterLimit}
            canPostAsAdmin={state.canPostAsAdmin}
            postAsAdmin={state.postAsAdmin}
            orgLabel={state.orgLabel}
            onTogglePostAsAdmin={state.setPostAsAdmin}
            allowAttachments={config.allowAttachments}
            attachmentTypesAllowed={config.attachmentTypesAllowed}
            attachments={state.attachmentsFor(item.id)}
            attachmentError={state.attachmentErrorFor(item.id)}
            onAddAttachments={(files) => state.addAttachments(item.id, files)}
            onRemoveAttachment={(i) => state.removeAttachment(item.id, i)}
            allowPrivate={config.allowPrivateComments}
            keepPrivate={state.keepPrivateFor(item.id)}
            onToggleKeepPrivate={(v) => state.setKeepPrivate(item.id, v)}
            onTextChange={(t) => state.setComposeText(item.id, t)}
            onPositionChange={(p) => state.setComposePosition(item.id, p)}
            onSubmit={() => state.submitComment(item.id)}
          />
        </div>
      )}
    </div>
  );
};

export default AgendaCommentRow;
